/**
 * AI メニュー開発 お気に入り一覧 + 試作フィードバック画面。
 * 詳細: docs/design/v02-ai-menu-development-requirements.md §7-1 (F-160系/F-165系)
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, Trash2, CheckCircle2, ClipboardCheck, Share2, EyeOff, X } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { StructuredProposalCard, type StructuredProposal } from '@/components/ai-menu/StructuredProposalCard';

// menu_name/content は2026-08-02のバグ修正で追加した構造化フィールド (メニュー名を必須入力にし、
// 本文を保存前に編集できるようにした)。raw_text はそれ以前に保存された旧形式との互換のため残す
// structured は v0.3 (S-9/S-11) で追加された propose_menu 由来の材料・レシピ工程データ (§7.1)。
// 保存はされていたが一覧/詳細のどちらでも参照しておらず「お気に入りのレシピ情報が欠落する」不具合になっていた
// v1.0 (2026-08-07): 私有化+共有 (docs/design/v03-ai-menu-favorites-privacy-sharing.md)。
// is_owner=falseの場合はshared_withは返らない (オーナーだけが共有先一覧・解除UIを見られる)
type SharedUser = { share_id: string; user_id: string; display_name: string | null };
type SharedStore = { share_id: string; node_id: string; name: string | null };
type Favorite = {
  favorite_id: string;
  proposal_id: string;
  registered_by: string | null;
  owner_name: string | null;
  is_owner: boolean;
  created_at: string;
  proposal_data: { menu_name?: string; content?: string; raw_text?: string; original_content?: string; structured?: StructuredProposal } | null;
  has_feedback: boolean;
  shared_with?: { users: SharedUser[]; stores: SharedStore[] };
};

type ShareContext = {
  users: { user_id: string; display_name: string | null }[];
  my_stores: { store_id: string; name: string }[];
};

/** contentがAI原文(original_content)から編集されているかどうか (QA/デザインレビュー反映: 編集有無を来歴として示す) */
const isEdited = (p: Favorite['proposal_data']): boolean =>
  Boolean(p?.original_content && p?.content && p.content !== p.original_content);

// design-and-codeレビュー反映 (HIGH): structuredはバックエンドでz.record(z.unknown())のまま無検証保存されており、
// StructuredProposalCardは元々「生成直後の信頼できるAI出力」専用に作られたコンポーネントだった。
// 任意時点のDB永続化データ(壊れたJSON・旧形式等)を渡す用途に転用したため、ここで最低限の形状チェックをしてから
// 渡す (欠落時はcard内部でのクラッシュ=白画面ではなく、テキストフォールバック表示に倒す)
const isRenderableStructured = (s: unknown): s is StructuredProposal => {
  if (!s || typeof s !== 'object') return false;
  const obj = s as Record<string, unknown>;
  return typeof obj.menu_name === 'string' && Array.isArray(obj.ingredients) && Array.isArray(obj.recipe_sections);
};
type Feedback = {
  feedback_id: string;
  taste_score: number;
  appearance_score: number;
  cost_score: number;
  difficulty_score: number;
  reproducibility_score: number;
  free_comment: string | null;
};

const SCORE_FIELDS: Array<{ key: keyof Omit<Feedback, 'feedback_id' | 'free_comment'>; label: string }> = [
  { key: 'taste_score', label: '味' },
  { key: 'appearance_score', label: '外観' },
  { key: 'cost_score', label: 'コスト感' },
  { key: 'difficulty_score', label: '難易度' },
  { key: 'reproducibility_score', label: '実現可能性' },
];

function FeedbackModal({ favoriteId, onClose }: { favoriteId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: existing } = useQuery<Feedback | null>({
    queryKey: ['ai-menu-feedback', favoriteId],
    queryFn: () => apiFetch<Feedback | null>(`/menu-favorites/${favoriteId}/feedback`),
  });

  const [scores, setScores] = useState<Record<string, number>>({
    taste_score: existing?.taste_score ?? 3,
    appearance_score: existing?.appearance_score ?? 3,
    cost_score: existing?.cost_score ?? 3,
    difficulty_score: existing?.difficulty_score ?? 3,
    reproducibility_score: existing?.reproducibility_score ?? 3,
  });
  const [comment, setComment] = useState(existing?.free_comment ?? '');

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/menu-favorites/${favoriteId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ ...scores, free_comment: comment || undefined }),
    }),
    onSuccess: () => {
      toast.success('試作フィードバックを保存しました');
      qc.invalidateQueries({ queryKey: ['ai-menu-favorites'] });
      qc.invalidateQueries({ queryKey: ['ai-menu-feedback', favoriteId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  return (
    <Modal
      open
      title="試作フィードバック"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-neutral-line text-sm">キャンセル</button>
          <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white text-sm font-medium disabled:opacity-50">
            {existing ? '更新' : '登録'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {SCORE_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-sm text-neutral-text">{f.label}: {scores[f.key]} / 5</span>
            <input
              type="range" min={1} max={5} step={1}
              value={scores[f.key]}
              onChange={(e) => setScores((s) => ({ ...s, [f.key]: Number(e.target.value) }))}
              className="mt-1 w-full"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-sm text-neutral-text">自由記述 (任意)</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={4}
            className="mt-1 w-full border border-neutral-line rounded px-3 py-2 text-sm"
          />
        </label>
      </div>
    </Modal>
  );
}

function ShareModal({ favorite, onClose }: { favorite: Favorite; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: context } = useQuery<ShareContext>({
    queryKey: ['ai-menu-favorite-share-context'],
    queryFn: () => apiFetch<ShareContext>('/menu-favorites/share-context'),
  });
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>(favorite.shared_with?.users ?? []);
  const [sharedStores, setSharedStores] = useState<SharedStore[]>(favorite.shared_with?.stores ?? []);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const seededRef = useRef(false);

  const alreadySharedUserIds = new Set(sharedUsers.map((u) => u.user_id));
  const alreadySharedStoreIds = new Set(sharedStores.map((s) => s.node_id));
  const availableUsers = (context?.users ?? []).filter((u) => !alreadySharedUserIds.has(u.user_id));
  const availableStores = (context?.my_stores ?? []).filter((s) => !alreadySharedStoreIds.has(s.store_id));

  // 所属する全店舗を共有先候補としてデフォルト事前選択 (本間さん判断、複数店舗所属時は全店舗)
  useEffect(() => {
    if (context && !seededRef.current) {
      setSelectedStoreIds(new Set(availableStores.map((s) => s.store_id)));
      seededRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const revokeMut = useMutation({
    mutationFn: (shareId: string) => apiFetch(`/menu-favorites/${favorite.favorite_id}/shares/${shareId}`, { method: 'DELETE' }),
    onSuccess: (_data, shareId) => {
      setSharedUsers((prev) => prev.filter((u) => u.share_id !== shareId));
      setSharedStores((prev) => prev.filter((s) => s.share_id !== shareId));
      qc.invalidateQueries({ queryKey: ['ai-menu-favorites'] });
      toast.success('共有を解除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '解除に失敗しました'),
  });

  const shareMut = useMutation({
    mutationFn: () => apiFetch(`/menu-favorites/${favorite.favorite_id}/shares`, {
      method: 'POST',
      body: JSON.stringify({ user_ids: [...selectedUserIds], node_ids: [...selectedStoreIds] }),
    }),
    onSuccess: () => {
      toast.success('共有しました');
      qc.invalidateQueries({ queryKey: ['ai-menu-favorites'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '共有に失敗しました'),
  });

  const toggle = (set: Set<string>, setter: (next: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const hasSelection = selectedUserIds.size > 0 || selectedStoreIds.size > 0;

  return (
    <Modal
      open
      title="お気に入りを共有"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-neutral-line text-sm">閉じる</button>
          <button
            type="button"
            onClick={() => shareMut.mutate()}
            disabled={!hasSelection || shareMut.isPending}
            className="px-4 py-2 rounded bg-brand-red text-white text-sm font-medium disabled:opacity-50"
          >
            共有する
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {(sharedUsers.length > 0 || sharedStores.length > 0) && (
          <div>
            <div className="text-xs font-medium text-neutral-sub mb-1.5">共有中</div>
            <ul className="space-y-1">
              {sharedUsers.map((u) => (
                <li key={u.share_id} className="flex items-center justify-between gap-2 text-sm bg-zinc-50 rounded px-2.5 py-1.5">
                  <span className="truncate min-w-0" title={u.display_name ?? u.user_id}>{u.display_name ?? u.user_id}</span>
                  <button type="button" onClick={() => revokeMut.mutate(u.share_id)} aria-label="共有解除" className="shrink-0 min-w-[44px] min-h-[44px] -my-2.5 flex items-center justify-center text-zinc-400 hover:text-danger-text">
                    <X size={14} />
                  </button>
                </li>
              ))}
              {sharedStores.map((s) => (
                <li key={s.share_id} className="flex items-center justify-between gap-2 text-sm bg-zinc-50 rounded px-2.5 py-1.5">
                  <span className="truncate min-w-0" title={`${s.name ?? s.node_id} (店舗全体)`}>{s.name ?? s.node_id} (店舗全体)</span>
                  <button type="button" onClick={() => revokeMut.mutate(s.share_id)} aria-label="共有解除" className="shrink-0 min-w-[44px] min-h-[44px] -my-2.5 flex items-center justify-center text-zinc-400 hover:text-danger-text">
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="text-xs font-medium text-neutral-sub mb-1.5">店舗単位で共有</div>
          {availableStores.length === 0 ? (
            <p className="text-xs text-zinc-400">共有可能な店舗がありません</p>
          ) : (
            <ul className="space-y-1">
              {availableStores.map((s) => (
                <li key={s.store_id}>
                  <label className="flex items-center gap-2 text-sm min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={selectedStoreIds.has(s.store_id)}
                      onChange={() => toggle(selectedStoreIds, setSelectedStoreIds, s.store_id)}
                    />
                    <span className="truncate" title={s.name}>{s.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="text-xs font-medium text-neutral-sub mb-1.5">人単位で共有</div>
          {availableUsers.length === 0 ? (
            <p className="text-xs text-zinc-400">共有可能な相手がいません</p>
          ) : (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {availableUsers.map((u) => (
                <li key={u.user_id}>
                  <label className="flex items-center gap-2 text-sm min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(u.user_id)}
                      onChange={() => toggle(selectedUserIds, setSelectedUserIds, u.user_id)}
                    />
                    <span className="truncate" title={u.display_name ?? u.user_id}>{u.display_name ?? u.user_id}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function AiMenuFavoritesPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  // ⚠️ 共有・削除は is_owner/isAdmin で行単位のガードをしているが、AiMenuConstraintsPageと同じ
  // 実機報告(2026-09-10)を受け、manage権限が無いユーザーにボタン自体を見せないよう追加で絞り込む
  // (manage権限降格後もis_owner=trueな旧お気に入りが残るケースで、押せないボタンが残っていたため)
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canManage = hasPermissionForFeatureAction('ai_menu_favorites', 'manage');
  const [feedbackTarget, setFeedbackTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<Favorite | null>(null);
  const [shareTarget, setShareTarget] = useState<Favorite | null>(null);

  const { data: favorites = [], isLoading } = useQuery<Favorite[]>({
    queryKey: ['ai-menu-favorites'],
    queryFn: () => apiFetch<Favorite[]>('/menu-favorites'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-favorites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('お気に入りを削除しました');
      qc.invalidateQueries({ queryKey: ['ai-menu-favorites'] });
      setDeleteTarget(null);
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  // 共有された側が自分の一覧から外す (人単位共有は解除、店舗単位共有は自分専用の非表示化。詳細はAPI側のコメント参照)
  const hideMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-favorites/${id}/hide`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('自分の一覧から外しました');
      qc.invalidateQueries({ queryKey: ['ai-menu-favorites'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '操作に失敗しました'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-brand-black flex items-center gap-2"><Star size={22} /> お気に入りメニュー</h1>
        <p className="text-sm text-zinc-500 mt-1">AI メニュー開発チャットで「お気に入り追加」した提案の一覧です</p>
      </div>

      {isLoading ? (
        <div className="text-sm text-neutral-sub">読込中…</div>
      ) : favorites.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 border border-zinc-100 rounded-xl bg-white shadow-sm text-sm">
          まだお気に入りが登録されていません
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {favorites.map((f) => (
            <div key={f.favorite_id} className="p-4 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full max-w-[160px] truncate" title={f.is_owner ? undefined : `${f.owner_name ?? '不明'}さんが共有`}>
                    {f.is_owner ? '自分の登録' : `${f.owner_name ?? '不明'}さんが共有`}
                  </span>
                  {f.has_feedback && (
                    <span className="inline-flex items-center gap-1 text-xs text-success-text bg-success-bg px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={12} /> 試作済み
                    </span>
                  )}
                  {isEdited(f.proposal_data) && (
                    <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full" title="AIの元の回答から内容が編集されています">編集済み</span>
                  )}
                </div>
                <div className="ml-auto shrink-0 flex items-center">
                  {f.is_owner && canManage && (
                    <button type="button" onClick={() => setShareTarget(f)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-zinc-400 hover:text-brand-black rounded-md hover:bg-zinc-50" aria-label="共有">
                      <Share2 size={16} />
                    </button>
                  )}
                  {!f.is_owner && (
                    <button type="button" onClick={() => hideMut.mutate(f.favorite_id)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-zinc-400 hover:text-brand-black rounded-md hover:bg-zinc-50" aria-label="自分の一覧から外す" title="自分の一覧から外す">
                      <EyeOff size={16} />
                    </button>
                  )}
                  {/* デザインレビュー反映 (2026-08-03): 削除操作の色は他画面 (IngredientsPage等) と統一し danger-text を使用 (旧 warning-text は alert/danger 分割で廃止) */}
                  {/* v1.0 (2026-08-07): F-164実装、削除は登録者本人または会社管理者のみ (バックエンドでも強制) */}
                  {((f.is_owner && canManage) || isAdmin) && (
                    <button type="button" onClick={() => setDeleteTarget(f.favorite_id)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-zinc-400 hover:text-danger-text rounded-md hover:bg-zinc-50" aria-label="削除">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              {/* デザインレビュー反映 (2026-08-02): カード本文と「詳細を見る」ボタンが同一アクションを二重提示していたため、
                  本文はクリック不可の静的表示にし「詳細を見る」ボタンに一本化 (Aesthetic and minimalist design) */}
              <div>
                <div className="text-[15px] font-semibold text-brand-black">
                  {f.proposal_data?.menu_name ?? <span className="text-zinc-400 font-normal">(メニュー名未設定・旧データ)</span>}
                </div>
                <p className="text-sm text-neutral-text line-clamp-4 whitespace-pre-wrap mt-1">
                  {f.proposal_data?.content ?? f.proposal_data?.raw_text ?? '(内容なし)'}
                </p>
              </div>
              <div className="text-xs text-zinc-400">{new Date(f.created_at).toLocaleString('ja-JP')}</div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDetailTarget(f)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-xs text-neutral-text hover:bg-zinc-50"
                >
                  詳細を見る
                </button>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setFeedbackTarget(f.favorite_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-xs text-neutral-text hover:bg-zinc-50"
                  >
                    <ClipboardCheck size={13} /> {f.has_feedback ? '試作FBを編集' : '試作FBを記録'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {feedbackTarget && <FeedbackModal favoriteId={feedbackTarget} onClose={() => setFeedbackTarget(null)} />}
      {shareTarget && <ShareModal favorite={shareTarget} onClose={() => setShareTarget(null)} />}

      <Modal
        open={detailTarget !== null}
        title={detailTarget?.proposal_data?.menu_name ?? 'お気に入りメニュー詳細'}
        onClose={() => setDetailTarget(null)}
      >
        <div className="space-y-4">
          {isRenderableStructured(detailTarget?.proposal_data?.structured) ? (
            <StructuredProposalCard proposal={detailTarget.proposal_data.structured} readOnly />
          ) : (
            <p className="text-sm text-neutral-text whitespace-pre-wrap">
              {detailTarget?.proposal_data?.content ?? detailTarget?.proposal_data?.raw_text ?? '(内容なし)'}
            </p>
          )}
          {detailTarget && isEdited(detailTarget.proposal_data) && (
            <div className="pt-3 border-t border-neutral-line">
              <div className="text-xs text-zinc-500 mb-1">この内容は保存時に編集されています。AIの元の回答:</div>
              <p className="text-xs text-zinc-400 whitespace-pre-wrap">{detailTarget.proposal_data?.original_content}</p>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="お気に入りを削除"
        message="この提案をお気に入りから削除します。よろしいですか？"
        confirmLabel="削除する"
        destructive
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
